// widgets/player/PlayerEngine.ts
// Чистый движок поверх HTMLVideoElement — без сетевых вызовов и без DOM-анимаций.
/**
 * Минимальный движок для работы с <video> и <audio>. Без jQuery.
 */
class PlayerEngine {
    constructor(el, events = {}) {
        this._loop = {};
        this._nextSeek = null; // отложенный seek до готовности метаданных
        // --- privates ---
        this._onTimeUpdate = () => {
            const t = this.currentTime();
            // автовозврат в начало лупа
            if (this._loop.out != null && t >= this._loop.out) {
                if (this._loop.in != null)
                    this.seek(this._loop.in);
            }
            this._events.timeupdate?.(t);
        };
        this._onDurationChange = () => {
            this._events.durationchange?.(this.duration());
            if (!this._media.seeking && this._nextSeek != null) {
                const ns = this._nextSeek;
                this._nextSeek = null;
                this.seek(ns);
            }
        };
        this._onProgress = () => {
            try {
                const end = this._media.buffered.end(0);
                this._events.progress?.(end);
            }
            catch { /* no-op */ }
        };
        // 2026-09-24: именованные обработчики, чтобы dispose() снимал все подписки
        this._onPlaying = () => this._events.playing?.();
        this._onPause = () => this._events.paused?.();
        this._onEnded = () => this._events.ended?.();
        this._onError = (e) => this._events.error?.(e);
        this._onRateChange = () => this._events.ratechange?.(this.playbackRate());
        this._onCanPlay = () => {
            if (this._nextSeek != null) {
                const ns = this._nextSeek;
                this._nextSeek = null;
                this.seek(ns);
            }
        };
        this._media = el;
        this._events = events;
        this._bind();
    }
    /** Загрузка src и (опционально) постера */
    load(url, poster) {
        if (this._media.src !== url) {
            try {
                this._media.pause();
            }
            catch { }
            this._media.src = url;
        }
        // постер — только для видео
        if (poster && this._media instanceof HTMLVideoElement) {
            this._media.poster = poster;
        }
    }
    play() { void this._media.play(); }
    pause() { this._media.pause(); }
    /** Текущая позиция с учётом отложенного seek */
    currentTime() {
        if (this._media.seeking && this._nextSeek != null)
            return this._nextSeek;
        return this._media.currentTime || 0;
    }
    duration() { return this._media.duration || 0; }
    /** 2026-09-24: воспроизведение приостановлено (в том числе до первого запуска) */
    isPaused() { return this._media.paused; }
    /** 2026-09-24: скорость воспроизведения */
    playbackRate() { return this._media.playbackRate || 1; }
    /** 2026-09-24: скорость 0.25–4; некорректное значение игнорируется */
    setPlaybackRate(rate) {
        if (!Number.isFinite(rate) || rate <= 0)
            return;
        this._media.playbackRate = Math.min(4, Math.max(0.25, rate));
    }
    /** Безопасный seek: откладываем, если metadata ещё не готовы */
    seek(sec) {
        if (Number.isNaN(this._media.duration)) {
            this._nextSeek = sec;
            return;
        }
        const clamped = Math.min(Math.max(0, sec), this._media.duration);
        try {
            this._media.currentTime = clamped;
            this._nextSeek = null;
        }
        catch {
            this._nextSeek = clamped;
        }
    }
    /** Установка лупа по in/out */
    setLoop(range) { this._loop = { ...range }; }
    setMuted(muted) { this._media.muted = muted; }
    setVolume(v) { this._media.volume = Math.max(0, Math.min(1, v)); }
    canPlayTypes() {
        const elem = document.createElement('video');
        const can = (t) => (elem.canPlayType ? (elem.canPlayType(t) || 'No') : 'No');
        const h264 = can('video/mp4');
        const webm = can('video/webm');
        return { h264: h264 || 'No', webm: webm || 'No' };
    }
    /** Снятие всех подписок */
    dispose() { this._unbind(); }
    _bind() {
        this._media.addEventListener('timeupdate', this._onTimeUpdate);
        this._media.addEventListener('durationchange', this._onDurationChange);
        this._media.addEventListener('progress', this._onProgress);
        this._media.addEventListener('playing', this._onPlaying);
        this._media.addEventListener('pause', this._onPause);
        this._media.addEventListener('ended', this._onEnded);
        this._media.addEventListener('error', this._onError);
        this._media.addEventListener('canplay', this._onCanPlay);
        this._media.addEventListener('ratechange', this._onRateChange);
    }
    _unbind() {
        this._media.removeEventListener('timeupdate', this._onTimeUpdate);
        this._media.removeEventListener('durationchange', this._onDurationChange);
        this._media.removeEventListener('progress', this._onProgress);
        this._media.removeEventListener('playing', this._onPlaying);
        this._media.removeEventListener('pause', this._onPause);
        this._media.removeEventListener('ended', this._onEnded);
        this._media.removeEventListener('error', this._onError);
        this._media.removeEventListener('canplay', this._onCanPlay);
        this._media.removeEventListener('ratechange', this._onRateChange);
    }
}

export { PlayerEngine };
